// useImpersonation Hook - User Impersonation Management
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { startImpersonation, endImpersonation } from '../../../Features/Authentication/authSlice';
import { useImpersonateUserMutation } from '../../../portals/admin/api/adminApiSlice';

/**
 * Hook for admin user impersonation functionality
 * Uses RTK Query for API calls
 *
 * @returns {Object} Impersonation methods and state
 */
export const useImpersonation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [impersonateMutation, { isLoading: isImpersonating }] = useImpersonateUserMutation();

  const {
    user,
    isImpersonating: isCurrentlyImpersonating,
    originalUser,
    impersonatedBy,
  } = useSelector((state) => state.auth);

  /**
   * Check if current user can impersonate others
   */
  const canImpersonate = useCallback(() => {
    if (!user) return false;

    // Only admin and super-admin portal users can impersonate
    return (
      user.assigned_portal === 'admin' ||
      user.assigned_portal === 'super-admin' ||
      user.is_superuser
    );
  }, [user]);

  /**
   * Impersonate a user using RTK Query mutation
   * @param {number} userId - Target user ID to impersonate
   * @param {string} targetPortal - Portal to navigate to (bureau or client)
   */
  const impersonateUser = useCallback(
    async (userId, targetPortal) => {
      if (!canImpersonate()) {
        throw new Error('You do not have permission to impersonate users');
      }

      try {
        // Call the RTK Query mutation
        const result = await impersonateMutation({
          userId,
          targetPortal,
        }).unwrap();

        // Dispatch impersonation action to update Redux state
        dispatch(startImpersonation(result));

        // Navigate to target portal
        const portalRoutes = {
          bureau: '/bureau',
          client: '/client',
          admin: '/admin',
          'super-admin': '/super-admin',
          sales: '/sales',
        };

        const targetRoute = portalRoutes[targetPortal] || '/';
        navigate(targetRoute);

        return result;
      } catch (error) {
        console.error('Impersonation error:', error);
        throw error;
      }
    },
    [canImpersonate, dispatch, navigate, impersonateMutation]
  );

  /**
   * Exit impersonation mode and return to original admin session
   */
  const exitImpersonation = useCallback(() => {
    if (!isCurrentlyImpersonating) {
      console.warn('Not currently impersonating');
      return;
    }

    // Dispatch end impersonation
    dispatch(endImpersonation());

    // Navigate back to admin portal
    navigate('/admin');
  }, [isCurrentlyImpersonating, dispatch, navigate]);

  return {
    // State
    isImpersonating: isCurrentlyImpersonating,
    originalUser,
    impersonatedUser: isCurrentlyImpersonating ? user : null,
    impersonatedBy,
    isLoading: isImpersonating,

    // Methods
    canImpersonate,
    impersonateUser,
    exitImpersonation,
  };
};

export default useImpersonation;
